import { skyPilotEndingPerformance } from './137-sky-pilot-cues.mjs';
import { skyTerrainHeight } from '../../../packages/domain/src/87-sky-terrain.mjs';
import { SKY_WORLD, skyPilotRootY } from '../../../packages/domain/src/86-sky-world-scale.mjs';
import { SKY_STAGES, skyEncounter } from '../../../packages/domain/src/76-sky-campaign.mjs';
import { skyBossDesign } from '../../../packages/domain/src/85-sky-boss-designs.mjs';
import { skyFramedHistoryCamera } from './163-sky-framed-view.mjs';
import { skyFlightPointToWorld } from '../../../packages/domain/src/92-sky-flight-frame.mjs';

const cathedralEnvelopes = new WeakMap();
function cathedralArrivalFrame(state, frame, aspect) {
  const pose = state.cathedral?.pose;
  const history = skyFramedHistoryCamera(state, aspect);
  if (!pose?.artwork || !history || frame.reduced || !(aspect > 0)) return null;
  let envelope = cathedralEnvelopes.get(pose);
  if (!envelope) {
    const min = { x: Infinity, y: Infinity, z: Infinity };
    const max = { x: -Infinity, y: -Infinity, z: -Infinity };
    for (const part of pose.parts)
      for (const x of [-part.halfExtents.x, part.halfExtents.x])
        for (const y of [-part.halfExtents.y, part.halfExtents.y])
          for (const z of [-part.halfExtents.z, part.halfExtents.z]) {
            const point = skyFlightPointToWorld(part.frame, { x, y, z });
            for (const axis of ['x', 'y', 'z']) {
              min[axis] = Math.min(min[axis], point[axis]);
              max[axis] = Math.max(max[axis], point[axis]);
            }
          }
    envelope = { min, max };
    cathedralEnvelopes.set(pose, envelope);
  }
  const centre = Object.fromEntries(
    ['x', 'y', 'z'].map((axis) => [axis, (envelope.min[axis] + envelope.max[axis]) / 2]),
  );
  const back = { x: 0.52, y: 0.16, z: Math.sqrt(1 - 0.52 ** 2 - 0.16 ** 2) };
  const rightLength = Math.hypot(back.x, back.z);
  const right = { x: back.z / rightLength, y: 0, z: -back.x / rightLength };
  const up = { x: back.y * right.z, y: rightLength, z: -back.y * right.x };
  const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
  const tangent = Math.tan((history.fov * Math.PI) / 360);
  let distance = 1;
  for (const x of [envelope.min.x, envelope.max.x])
    for (const y of [envelope.min.y, envelope.max.y])
      for (const z of [envelope.min.z, envelope.max.z]) {
        const offset = { x: x - centre.x, y: y - centre.y, z: z - centre.z };
        distance = Math.max(
          distance,
          dot(offset, back) +
            Math.max(
              Math.abs(dot(offset, right)) / (tangent * aspect * 0.78),
              Math.abs(dot(offset, up)) / (tangent * 0.76),
            ),
        );
      }
  const weight = 1 - frame.settle;
  const mix = (a, b) => a + (b - a) * weight;
  return {
    cameraX: mix(history.x, centre.x + back.x * distance),
    cameraY: mix(history.y, centre.y + back.y * distance),
    cameraZ: mix(history.z, centre.z + back.z * distance),
    lookX: mix(history.lookX, centre.x),
    lookY: mix(history.lookY, centre.y),
    lookZ: mix(history.lookZ, centre.z),
    fov: history.fov,
    up: { x: 0, y: 1, z: 0 },
  };
}

/** Presentation-only camera script, in visible active seconds. It never advances
 * combat, rewards or purchases; reduced motion uses a short stationary title. */
export function skyCinematicFrame(kind, age, reduced = false, clipDurations = {}) {
  if (['pilot-down', 'guardian-escape', 'expedition-complete'].includes(kind))
    return skyEndingFrame(kind, age, reduced, clipDurations);
  const performance = skyPilotEndingPerformance(kind, age, reduced, clipDurations);
  const duration = reduced
    ? 0.8
    : kind === 'arrival'
      ? 4.8
      : Math.max(5.4, performance.minimumDuration);
  const t = Math.max(0, Math.min(1, age / duration));
  const ease = t * t * (3 - 2 * t);
  return {
    done: t >= 1,
    duration,
    clipDurations,
    reduced,
    shot: t < 0.32 ? 'vista' : t < 0.74 ? 'hero' : 'launch',
    progress: t,
    settle: reduced
      ? 1
      : Math.max(0, Math.min(1, (t - 0.74) / 0.26)) ** 2 *
        (3 - 2 * Math.max(0, Math.min(1, (t - 0.74) / 0.26))),
    cameraX: reduced ? 0 : Math.sin(ease * Math.PI) * (kind === 'arrival' ? 8 : -8),
    cameraY: reduced ? 5.4 : 7.5 - ease * 2.1,
    cameraZ: reduced ? 17 : 7 + ease * 10,
    lookY: reduced ? 4.4 : 2.8 + ease * 1.6,
    lookZ: reduced ? -40 : -2 - ease * 38,
    riderY: reduced ? 0 : 1.3 + Math.sin(t * Math.PI) * 0.9,
  };
}

const smooth = (value) => {
  const t = Math.max(0, Math.min(1, value));
  return t * t * t * (t * (t * 6 - 15) + 10);
};

/** Compose committed endings around the actual grounded/landing performer.
 * Metre-based distance expands for narrow viewports; the low target leaves the
 * lower caption region clear without projecting a nearby actor off an orbit. */
export function skyTerminalCameraFrame(state, frame, aspect) {
  if (!frame) return null;
  if (frame.kind === 'arrival') return cathedralArrivalFrame(state, frame, aspect);
  if (state.framedEnding) {
    const live = state.leviathan;
    const camera = skyFramedHistoryCamera(
      {
        ...state,
        leviathan: {
          ...live,
          encounter: { ...live.encounter, phase: live.terminalPhase ?? live.phase },
        },
      },
      aspect,
    );
    if (camera)
      return {
        cameraX: camera.x,
        cameraY: camera.y,
        cameraZ: camera.z,
        lookX: camera.lookX,
        lookY: camera.lookY,
        lookZ: camera.lookZ,
        up: camera.up,
        fov: camera.fov,
        near: camera.near,
      };
  }
  const ground = skyTerrainHeight(state.x, SKY_WORLD.pilotZ, state.terrainTravel ?? 0);
  const rootY = skyPilotRootY(state.y, ground);
  const distance = Math.max(7.2, 3.5 / Math.max(0.25, aspect));
  const orbit = frame.reduced ? 0 : Math.sin(frame.progress * Math.PI) * 0.8;
  return {
    cameraX: state.x + orbit,
    cameraY: rootY + 2.8,
    cameraZ: SKY_WORLD.pilotZ + distance,
    lookX: state.x,
    lookY: rootY - 0.45,
    lookZ: SKY_WORLD.pilotZ,
  };
}

/** Defeat framing follows the actual guardian position in world metres. The
 * domain owns the safe, input-free death interval; this camera only showcases
 * its stagger, collapse and dissipation. Zero endpoint velocity prevents a snap
 * into or out of normal play. Reduced motion keeps the normal camera entirely. */
export function skyGuardianDefeatFrame(boss, aspect = 1, reduced = false) {
  if (!boss?.defeated || reduced) return null;
  const progress = Math.max(0, Math.min(1, (boss.deathAge ?? 0) / (boss.deathDuration || 3.2)));
  const weight = smooth(progress / 0.24) * (1 - smooth((progress - 0.76) / 0.24));
  const large = ['leviathan', 'twin', 'skeletal'].includes(boss.form);
  const distance = (large ? 34 : 26) * (aspect < 1 ? 1.3 : 1);
  const angle = -0.22 + 0.38 * smooth(progress);
  return {
    progress,
    weight,
    shot: progress < 0.25 ? 'stagger' : progress < 0.6 ? 'collapse' : 'dissipate',
    cameraX: boss.x + Math.sin(angle) * distance,
    cameraY: Math.max(3, boss.y + (large ? 5 : 3)),
    cameraZ: boss.z + Math.cos(angle) * distance,
    lookX: boss.x,
    lookY: Math.max(0.5, boss.y - smooth(progress) * 1.2),
    lookZ: boss.z,
  };
}

/** Keep the shoulder direction fixed while framing the entry. Default encounters
 * pull back for passing wings; the local prototype dollies toward a physical stop
 * in front of the rider. Neither changes steering or combat time. */
export function skyGuardianFlybyFrame(boss, duration, reduced = false) {
  if (!boss || boss.defeated || reduced || !(duration > 0)) return null;
  const age = Math.max(0, boss.age ?? 0);
  if (age >= duration) return null;
  const progress = age / duration;
  const weight = smooth(progress / 0.3) * (1 - smooth((progress - 0.7) / 0.3));
  if (boss.artVariant === 'first-dragon') {
    // The dragon stops in front of the rider. A bounded dolly supplies the close
    // face study without moving its physical mesh through the player plane.
    const close = smooth((age - 0.8) / 0.8) * (1 - smooth((age - 2.15) / 0.95));
    return {
      weight,
      pullback: weight * 0.4 - close * 0.35,
      lift: weight * 0.12 + close * 0.08,
      fov: close ? -close * 2 : 0,
    };
  }
  return { weight, pullback: weight * 0.4, lift: weight * 0.12, fov: 0 };
}

/** Map a committed simulation outcome to its presentation. A zero-shield loss
 * and a timed-out guardian are distinct, without parsing localized event text. */
export function skyEndingKind(state) {
  if (state.status === 'intermission') return 'departure';
  if (state.status === 'complete') return 'expedition-complete';
  if (state.status === 'over') return state.lives <= 0 ? 'pilot-down' : 'guardian-escape';
  return null;
}

function skyEndingFrame(kind, age, reduced, clipDurations = {}) {
  const performance = skyPilotEndingPerformance(kind, age, reduced, clipDurations);
  const duration = reduced
    ? 1.2
    : Math.max(
        kind === 'expedition-complete' ? 7 : kind === 'pilot-down' ? 4.8 : 5.6,
        performance.minimumDuration,
      );
  const progress = Math.max(0, Math.min(1, age / duration));
  const travel = reduced ? 0 : smooth(progress);
  const down = kind === 'pilot-down';
  const escape = kind === 'guardian-escape';
  return {
    done: progress >= 1,
    duration,
    clipDurations,
    progress,
    settle: reduced ? 1 : 0,
    shot: progress < 0.25 ? 'aftermath' : progress < 0.72 ? 'resolution' : 'epilogue',
    cameraX: reduced ? 0 : (down ? 2 : -4) * Math.sin(travel * Math.PI),
    cameraY: reduced ? 5.4 : 5.4 - travel * 2.6,
    cameraZ: reduced ? 17 : 17 - travel * 8,
    lookY: reduced ? 4.4 : 4.4 - travel * 3.3,
    lookZ: reduced ? -40 : down ? -1 : -40 + travel * 41,
    riderY: 0,
    riderFall: down ? travel : 0,
    riderAdvance: 0,
    riderRoll: 0,
    riderPitch: 0,
    bossRetreat: escape ? travel : 0,
    followRider: true,
    reduced,
  };
}

/** Arrival lens and actor share the current stage's physical lane offset.
 * Completed-sector endings retain their own terrain/framed presentation. */
export function skyCinematicLaneY(state) {
  const lane = state.journeyAltitude;
  return lane && lane.sector === state.sector && Number.isFinite(lane.originY) ? lane.originY : 0;
}

/** Derive a cinematic render snapshot; never mutate run state or award outcomes.
 * Terminal scenes remove frozen projectiles and sample travel in visible seconds.
 * The completed sector remains on screen until its debrief/next launch. */
export function skyCinematicState(state, frame) {
  if (!frame) return state;
  if (
    state.leviathan &&
    state.flightFrame &&
    ['pilot-down', 'guardian-escape'].includes(frame.kind)
  )
    return {
      ...state,
      // An interior has no world-ground landing path. Keep the actual failed
      // pose, contact geometry and reviewed history lens while the finite
      // outcome/power-down sequence plays; never drop through the hull floor.
      framedEnding: true,
      status: 'cinematic',
      presentationPose: null,
      presentationAge: 0,
      propulsionScale: 1 - smooth(frame.progress),
      grace: 0,
      pulseTime: 0,
      shots: [],
      threats: [],
      rings: [],
      bursts: [],
    };
  const groundAim =
    skyTerrainHeight(state.x, SKY_WORLD.pilotZ, state.terrainTravel ?? 0) +
    SKY_WORLD.pilotAimHeight;
  if (frame.kind === 'arrival')
    return {
      ...state,
      time: state.time + (frame.flightAge ?? frame.age),
      x: state.x * frame.settle,
      y:
        ((skyCinematicLaneY(state) || groundAim - SKY_WORLD.pilotAimHeight) +
          SKY_WORLD.pilotAimHeight +
          frame.riderY) *
          (1 - frame.settle) +
        state.y * frame.settle,
      locomotion: frame.settle > 0.9 ? state.locomotion : 'fly',
      status: 'cinematic',
    };
  const boss = frame.scene?.boss;
  const retreat = frame.bossRetreat ?? 0;
  const performance = skyPilotEndingPerformance(
    frame.kind,
    frame.age,
    frame.reduced,
    frame.clipDurations,
  );
  const landing = frame.reduced ? 1 : smooth(frame.age / (performance?.touchdown || 1));
  const airborne = frame.kind === 'pilot-down' && Boolean(skyCinematicLaneY(state));
  const fall = frame.reduced
    ? 0
    : Math.min(3, Math.max(0, state.y - groundAim - 1.8)) * Math.min(1, smooth(frame.progress));
  return {
    ...state,
    ...frame.scene,
    time: state.time + (frame.reduced ? 0 : frame.age),
    y: airborne
      ? state.y - fall
      : landing >= 1
        ? groundAim
        : state.y - (state.y - groundAim) * landing,
    // Straighten into the grounded rest before starting a full-body performance;
    // mixing a flight tuck into a collapse can push the skin below the surface.
    locomotion: airborne
      ? 'fly'
      : frame.reduced || frame.age >= (performance?.touchdown || 1) - 0.3
        ? 'idle'
        : 'fly',
    propulsionScale: 1 - (airborne ? smooth(frame.progress) : landing),
    presentationPose: airborne ? 'Flight' : performance?.active ? performance.name : null,
    presentationAge: airborne ? Math.min(frame.age, 0.8) : (performance?.age ?? 0),
    status: 'cinematic',
    grace: 0,
    pulseTime: 0,
    enemies: [],
    shots: [],
    threats: [],
    rings: [],
    bursts: [],
    boss:
      boss && frame.kind === 'guardian-escape'
        ? {
            ...boss,
            age: boss.age + (frame.reduced ? 0 : frame.age),
            z: boss.z - retreat * 105,
            y: boss.y + retreat * 18,
            anatomy: null,
            weakpoint: null,
            weakpoints: [],
            charge: 0,
            recoil: 0,
            lastAttackAge: -100,
            vx: 0,
            vy: 0,
            attackPhase: 'retreat',
            vulnerable: false,
          }
        : null,
  };
}

/** Captions describe the committed result, separate from the upcoming sector.
 * Short, stable stage names and outcome copy remain visible with reduced motion. */
export function skyCinematicCopy(kind, frame, state) {
  if (kind === 'arrival') {
    if (state.leviathan || SKY_STAGES[state.sector]?.encounterId === 'gilded-leviathan')
      return {
        label: `SECTOR ${String(state.sector + 1).padStart(2, '0')} / INBOUND`,
        title: frame.shot === 'launch' ? 'READY TO FLY' : 'THE GILDED LEVIATHAN',
        copy: '◎ AIM · SHOOT · ↔ MOVE',
      };
    const design =
      SKY_STAGES[state.sector]?.briefing ?? skyBossDesign(skyEncounter(state.sector)?.bossDesign);
    const stage = SKY_STAGES[state.sector];
    return {
      label: `SECTOR ${String(state.sector + 1).padStart(2, '0')} / ${stage.bossPreview ? 'WORLD PREVIEW' : 'INBOUND'}`,
      title: frame.shot === 'launch' ? 'READY TO FLY' : SKY_STAGES[state.sector].name,
      copy:
        state.sector === 0
          ? '◎ AIM · SHOOT · ↔ MOVE'
          : design
            ? `${stage.description} ${stage.bossPreview && !stage.authoredBoss ? `Practice guardian: ${stage.previewBossName}. Signature battle in development. ` : ''}${design.strength}. ${design.weakness}.`
            : stage.description,
    };
  }
  if (kind === 'departure')
    return {
      label: `${state.stageResult?.name ?? 'SAFE PASSAGE'} / COMPLETE`,
      title: frame.shot === 'launch' ? 'RETURNING TO DOCK' : 'SECTOR CLEAR',
      copy: `+${state.stageResult?.score.toLocaleString() ?? 0} POINTS · ${state.salvage} SALVAGE RECOVERED`,
    };
  const final = kind === 'expedition-complete';
  const down = kind === 'pilot-down';
  return {
    label: final
      ? 'ALL BEACONS RESTORED / EXPEDITION COMPLETE'
      : down
        ? 'SHIELDS DEPLETED / FLIGHT LOST'
        : 'SIGNAL FADING / GUARDIAN ESCAPED',
    title: final ? 'FRONTIER RESTORED' : down ? 'PILOT DOWN' : 'THE GUARDIAN WITHDRAWS',
    copy: final
      ? `${SKY_STAGES.length} worlds connected. ${state.guardiansDefeated} guardians defeated. Your flight has brought the light home.`
      : down
        ? 'The boosters fade. Your expedition ends here; a new flight awaits when you are ready.'
        : `${state.boss?.name ?? 'The guardian'} retreats beyond the sky route. Regroup for another expedition.`,
  };
}
